import { httpResource } from '@angular/common/http'
import { computed, Injectable } from '@angular/core'

/** Aufgelöste Geo-Information zu einer PLZ. */
export interface PlzGeo {
  readonly zip: string
  readonly lat: number
  readonly lon: number
  readonly city: string
  readonly bundesland: string
}

/** Ein Ring als Liste von `[lon, lat]`. */
export type GeoRing = readonly (readonly [number, number])[]

/**
 * Die Bundesland-GeoJSON, soweit wir sie lesen. ECharts (interne Lieferlandkarte) braucht nur den
 * Namen, die SVG-Karte der Portal-Startseite zeichnet die Umrisse selbst und liest die Geometrie.
 */
export interface BundeslaenderGeoJson {
  readonly type: string
  readonly features: readonly {
    readonly properties: { readonly name: string }
    readonly geometry?:
      | { readonly type: 'Polygon'; readonly coordinates: readonly GeoRing[] }
      | { readonly type: 'MultiPolygon'; readonly coordinates: readonly (readonly GeoRing[])[] }
  }[]
}

const PLZ_ASSET_URL = 'assets/geo/plz-koordinaten.csv'
const GEOJSON_ASSET_URL = 'assets/geo/deutschland-bundeslaender.geo.json'

/**
 * Löst PLZ zu Koordinaten auf und liefert die Bundesland-Umrisse für die Karte.
 *
 * Beide Assets liegen im Repo (siehe `libs/shared/public/assets/geo/QUELLEN.md`) und werden erst
 * geladen, wenn jemand den Dienst injiziert: die interne Lieferlandkarte und die Karte „Wo Ihre
 * Patient:innen wohnen“ auf der Startseite des Portals. Kein externes Geocoding, keine
 * Fremd-Requests, und damit gehen auch keine Adressdaten nach draußen.
 */
@Injectable({
  providedIn: 'root',
})
export class PlzGeoService {
  readonly plzTable = httpResource.text(() => PLZ_ASSET_URL, { defaultValue: '' })
  readonly bundeslaender = httpResource<BundeslaenderGeoJson>(() => GEOJSON_ASSET_URL)

  readonly byZip = computed<ReadonlyMap<string, PlzGeo>>(() => parsePlzTable(this.plzTable.value()))
}

/**
 * Parst das PLZ-Asset. Format (Details in `QUELLEN.md`):
 *
 * ```
 * # Kommentar
 * @laender Baden-Württemberg|Bayern|…
 * 10115;52.532;13.385;Berlin;2
 * ```
 */
export function parsePlzTable(raw: string): ReadonlyMap<string, PlzGeo> {
  const result = new Map<string, PlzGeo>()
  if (!raw) return result

  let laender: string[] = []

  for (const line of raw.split('\n')) {
    if (!line || line.startsWith('#')) continue

    if (line.startsWith('@laender ')) {
      laender = line.slice('@laender '.length).trim().split('|')
      continue
    }

    const [zip, lat, lon, city, landIdx] = line.split(';')
    if (!zip || !lat || !lon) continue

    const latitude = Number.parseFloat(lat)
    const longitude = Number.parseFloat(lon)
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue

    result.set(zip, {
      zip,
      lat: latitude,
      lon: longitude,
      city: city ?? '',
      bundesland: laender[Number.parseInt(landIdx ?? '-1', 10)] ?? '',
    })
  }

  return result
}
